import React, { Fragment, useEffect, useRef, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faX } from '@fortawesome/free-solid-svg-icons'

// COMPONENTS
import FullPageLoader from '../../components/Layout/FullPageLoader'
import FilterBar from './FilterBar'
import CompletedSalesTable from './CompletedSalesTable'
import AllAgentListingTable from './AllAgentListingTable'
import CommissionListingTable from './CommissionListingTable'
import CommissionListingAgentTable from './CommissionListingAgentTable'

// DATA
import agentComponent from '../../businesses/agent'

const SalesPerformanceModal = (props) => {
  const agentID = props?.agentID || ''
  const tableContainer = useRef(null)

  const [loading, setLoading] = useState(false)
  const [isFetching, setIsFetching] = useState(false)
  const [filterData, setFilterData] = useState(null)
  const [commissionType, setCommissionType] = useState(null)
  const [listedBy, setListedBy] = useState('Branch')

  // SELECTED FOR DATA TYPE
  const [selectedCompletedSales, setSelectedCompletedSales] = useState(null)
  const [selectedAgent, setSelectedAgent] = useState(null)
  const [selectedBranch, setSelectedBranch] = useState(null)

  // DATA FOR TABLE
  const [completedSalesByBranchGroupData, setCompletedSalesByBranchGroupData] = useState(null)
  const [totalAgentRecruitListBy, setTotalAgentRecruitListBy] = useState(null)
  const [agentCompletedSalesByDateData, setAgentCompletedSalesByDateData] = useState(null)
  const [completedSalesCommissionByUserIDData, setCompletedSalesCommissionByUserIDData] = useState(null)

  const handleFilter = (data) => {
    const filterData = { ...data }
    filterData.StartDate = data.StartDate.toISOString()
    filterData.EndDate = data.EndDate.toISOString()

    setListedBy(filterData.LiveListBy)
    setCompletedSalesByBranchGroupData(null)
    setTotalAgentRecruitListBy(null)
    setAgentCompletedSalesByDateData(null)
    setCompletedSalesCommissionByUserIDData(null)
    setSelectedCompletedSales(null)

    // RUN THE FILTERING WITH THE filterData
    setFilterData(filterData)
    setIsFetching(!isFetching)
  }

  const handleSelectCompletedSales = async (item, caseType) => {
    const Group = item?.CommissionPayToUserBranch || null
    const BranchID = item?.CommissionPayToUserBranchID || null
    
    setSelectedCompletedSales(item)
    setCommissionType(caseType)
    setAgentCompletedSalesByDateData(null)
    setTotalAgentRecruitListBy(null)
    setCompletedSalesCommissionByUserIDData(null)
    setSelectedAgent(null)

    let agentCompletedSalesByDate

    if (!caseType) {
      agentCompletedSalesByDate = await agentComponent.getAgentCompletedSalesByDate(agentID, filterData, Group, BranchID)
    } else {
      agentCompletedSalesByDate = await agentComponent.getAgentCompletedSalesByCaseType(agentID, filterData, Group, BranchID, caseType)
    }

    if (agentCompletedSalesByDate.length > 0) {
      setAgentCompletedSalesByDateData(agentCompletedSalesByDate)
    }
  }

  const handleSelectAgentRecruit = async (item) => {
    const Group = item?.CommissionPayToUserBranch || null
    const BranchID = item?.CommissionPayToUserBranchID || null
    
    setSelectedBranch(item)
    setTotalAgentRecruitListBy(null)
    setAgentCompletedSalesByDateData(null)
    setCompletedSalesCommissionByUserIDData(null)
    setSelectedAgent(null)

    const totalAgentRecruitListBy = await agentComponent.getTotalAgentRecruitListBy(agentID, filterData, Group, BranchID)

    if (totalAgentRecruitListBy.length > 0) {
      setTotalAgentRecruitListBy(totalAgentRecruitListBy)
    }
  }

  const handleSelectAgent = async (agent) => {
    const agentID = agent?.CommissionPayToUserID
    setSelectedAgent(agent)
    setCompletedSalesCommissionByUserIDData(null)

    const agentCompletedSalesBySalesDate = await agentComponent.getAgentCompletedSalesBySalesDate(agentID, filterData, commissionType)

    if (agentCompletedSalesBySalesDate.length > 0) {
      setCompletedSalesCommissionByUserIDData(agentCompletedSalesBySalesDate)
    }
  }

  useEffect(() => {
    if (agentID && filterData) {
      (async () => {
        setLoading(true)
        const completedSalesByBranchGroup = await agentComponent.getCompletedSalesByBranchGroup(agentID, filterData)
        console.log('completedSalesByBranchGroup >', completedSalesByBranchGroup);

        if (completedSalesByBranchGroup.length > 0) {
          setCompletedSalesByBranchGroupData(completedSalesByBranchGroup)
          setLoading(false)
        } else {
          setLoading(false)
        }
      })()
    }
  }, [isFetching])

  useEffect(() => {
    if (totalAgentRecruitListBy || agentCompletedSalesByDateData || completedSalesCommissionByUserIDData) {
      tableContainer.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      })
    }
  }, [totalAgentRecruitListBy, agentCompletedSalesByDateData, completedSalesCommissionByUserIDData])

  return (
    <Transition appear show={ props.showModal } as={ Fragment }>
      <Dialog  as="div" className="relative z-40" onClose={ () => props.setShowModal(false) }>
        <Transition.Child
          as={ Fragment }
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="h-full md:p-5 p-2">
            <Transition.Child
              as={ Fragment }
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full h-full transform overflow-hidden rounded-2xl bg-white shadow-2xl transition-all">
                <div className='w-full h-full bg-secondary-light flex flex-col'>
                  <div className="w-full py-3.5 px-4 bg-primary text-white flex items-center relative">
                    <div className='w-full flex items-center justify-between'>
                      <p>Sales Performance Report</p>

                      <button onClick={ () => props.setShowModal(false) } className='cursor-pointer px-2 hover:opacity-70 focus-visible:outline-none'>
                        <FontAwesomeIcon icon={ faX } className='h-3 w-3' />
                      </button>
                    </div>
                  </div>

                  <FilterBar
                    onHandleFilter={ handleFilter }
                  />
                  
                  <div className='flex-1 w-full relative'>
                    <div className='absolute top-0 left-0 w-full h-full overflow-auto hide-scrollbar md:p-5 p-3'>
                      <div className='w-full h-full flex flex-col'>
                        <FullPageLoader loading={ loading } />

                        {
                          completedSalesByBranchGroupData === null &&
                          <div className='w-full h-full flex flex-col text-center items-center justify-center text-gray-400'>
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={0.5} stroke="currentColor" className="w-20 h-20">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5m6 4.125l2.25 2.25m0 0l2.25 2.25M12 13.875l2.25-2.25M12 13.875l-2.25 2.25M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
                            </svg>

                            <p>No data</p>
                          </div>
                        }

                        <div ref={ tableContainer } className='flex flex-col md:gap-4 gap-2 pb-2 md:pb-4'>
                          {
                            completedSalesByBranchGroupData &&
                            <div className='flex flex-col h-full w-full'>
                              <CompletedSalesTable
                                data={ completedSalesByBranchGroupData }
                                listedBy={ listedBy }
                                onSelect={ handleSelectCompletedSales }
                                onSelectAgent={ handleSelectAgentRecruit }
                              />
                            </div>
                          }

                          {
                            totalAgentRecruitListBy &&
                            <div className='flex flex-col h-full w-full'>
                              <AllAgentListingTable
                                data={ totalAgentRecruitListBy }
                                selected={ selectedBranch }
                              />
                            </div>
                          }
                          
                          {
                            agentCompletedSalesByDateData &&
                            <div className='flex flex-col h-full w-full'>
                              <CommissionListingTable
                                data={ agentCompletedSalesByDateData }
                                selected={ selectedCompletedSales }
                                commissionType={ commissionType }
                                onSelect={ handleSelectAgent }
                              />
                            </div>
                          }

                          {
                            completedSalesCommissionByUserIDData &&
                            <div className='flex flex-col h-full w-full'>
                              <CommissionListingAgentTable
                                data={ completedSalesCommissionByUserIDData }
                                selected={ selectedAgent }
                                commissionType={ commissionType }
                              />
                            </div>
                          }
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export default SalesPerformanceModal